import { Box, Button, IconButton, Menu, MenuItem, Modal, Typography } from '@mui/material';
import React, { useState } from 'react';
import { TaskItemBlockProps } from './types';
import StartOutlinedIcon from '@mui/icons-material/StartOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import CircleOutlinedIcon from '@mui/icons-material/CircleOutlined';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useTranslation } from 'react-i18next';

const LINK_PATTERN = /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g;

const renderWithLinks = (text: string, baseId: string) => {
  const parts = text.split(LINK_PATTERN);
  return parts.map((part, index) => {
    if (index % 3 === 0) return part;
    if (index % 3 === 2) return null;
    return (
      <a
        id={`${baseId}TextLink_${index}`}
        key={`${baseId}TextLink_${part}`}
        href={parts[index + 1]}
        target='_blank'
        rel='noopener noreferrer'
        onClick={(e) => e.stopPropagation()}
        style={{ textDecoration: 'underline' }}
      >
        {part}
      </a>
    );
  });
};
const TaskItem = ({
  id,
  iconLeft,
  taskCompleted,
  taskText,
  status,
  date,
  actions,
  onAction,
  onComplete,
  onContinue,
}: TaskItemBlockProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const open = Boolean(anchorEl);
  const { t } = useTranslation();

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleComplete = () => {
    handleMenuClose();
    if (status === 'in_progress' || status === 'overdue') {
      if (onComplete) onComplete();
    }
  };
  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleDelete = () => {
    setDeleteConfirmOpen(true);
    handleMenuClose();
  };

  const handleConfirmDelete = () => {
    if (onAction) {
      onAction('delete');
    }
    setDeleteConfirmOpen(false);
    handleMenuClose();
  };

  const handleCancelDelete = () => {
    setDeleteConfirmOpen(false);
  };

  const handleModify = () => {
    if (onAction) {
      onAction('modify');
    }
    handleMenuClose();
  };

  const handleContinue = () => {
    if (onContinue) onContinue();
    handleMenuClose();
  };

  // Fixed 3-letter table instead of Intl's locale-derived abbreviation: en-GB/en-AU
  // report September as the 4-letter "Sept" while every other month is 3 letters,
  // which silently breaks any UI/test code that assumes a uniform "DD MMM YYYY" shape.
  const MONTHS_SHORT = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];

  function formatDate(date: Date): string {
    const day = String(date.getDate()).padStart(2, '0');
    const month = MONTHS_SHORT[date.getMonth()];
    return `${day} ${month} ${date.getFullYear()}`;
  }
  return (
    <Box
      className={
        'task-item' +
        (taskCompleted || status === 'completed' ? ' completed' : '') +
        (iconLeft ? ' icon-left' : '')
      }
      sx={{ display: 'flex', alignItems: 'center' }}
    >
      <div style={{ display: 'flex', gap: '6px', flexGrow: 1 }}>
        {!status && taskCompleted && <CheckCircleIcon fontSize='small' className='check-icon' />}
        {!status && !taskCompleted && <CircleOutlinedIcon fontSize='small' />}
        <div style={{ marginTop: '0.25rem' }}>
          {renderWithLinks(taskText, id ?? 'taskItemBlock')}
        </div>
      </div>
      {!status && !taskCompleted && <StartOutlinedIcon fontSize='small' />}
      {status === 'overdue' && iconLeft && (
        <IconButton
          id={`${id ?? 'taskItemBlock'}TaskButtonIconBtn`}
          onClick={handleComplete}
          className='task-item-icon-button'
          aria-label='Task button'
        >
          <CheckBoxOutlineBlankIcon
            id={`${id ?? 'taskItemBlock'}TaskButtonIconBtnRedCircleIcon`}
            fontSize='medium'
            className='overdue-icon'
          />
        </IconButton>
      )}

      {status === 'in_progress' && iconLeft && (
        <IconButton
          id={`${id ?? 'taskItemBlock'}CompleteIconBtn`}
          onClick={handleComplete}
          className='task-item-icon-button'
        >
          <CheckBoxOutlineBlankIcon
            id={`${id ?? 'taskItemBlock'}CompleteIconBtnCircleIcon`}
            fontSize='medium'
          />
        </IconButton>
      )}

      {status === 'completed' && iconLeft && (
        <CheckBoxIcon fontSize='small' className='check-icon' />
      )}

      <Box
        className='date-container'
        sx={{ width: { xs: 'auto', sm: '180px' }, flexShrink: 0, justifyContent: 'flex-end' }}
      >
        {date && status !== 'completed' && (
          <>
            <CalendarMonthOutlinedIcon fontSize='small' />
            <Typography className='date-text' sx={{ whiteSpace: 'nowrap' }}>
              {formatDate(date)}
            </Typography>
          </>
        )}
        {actions && (
          <Box className='actions-container'>
            <IconButton
              id={`${id ?? 'taskItemBlock'}MenuButtonIconBtn`}
              aria-label='Menu button'
              onClick={handleMenuOpen}
              className='morevert-icon'
              disableRipple
            >
              <MoreVertIcon
                id={`${id ?? 'taskItemBlock'}MenuButtonIconBtnMoreVertIcon`}
                fontSize='small'
              />
            </IconButton>
            <Menu anchorEl={anchorEl} open={open} onClose={handleMenuClose}>
              {status === 'completed'
                ? [
                    <MenuItem
                      id={`${id ?? 'taskItemBlock'}ContinueTaskMenuItem`}
                      key='continue'
                      onClick={handleContinue}
                    >
                      Continue task
                    </MenuItem>,
                    <MenuItem
                      id={`${id ?? 'taskItemBlock'}DeleteMenuItem`}
                      key='delete'
                      onClick={handleDelete}
                    >
                      Delete
                    </MenuItem>,
                  ]
                : [
                    <MenuItem
                      id={`${id ?? 'taskItemBlock'}CompleteMenuItem`}
                      key='complete'
                      onClick={handleComplete}
                    >
                      Complete
                    </MenuItem>,
                    <MenuItem
                      id={`${id ?? 'taskItemBlock'}ChangeMenuItem`}
                      key='change'
                      onClick={handleModify}
                    >
                      Change
                    </MenuItem>,
                    <MenuItem
                      id={`${id ?? 'taskItemBlock'}DeleteMenuItem2`}
                      key='delete'
                      onClick={handleDelete}
                    >
                      Delete
                    </MenuItem>,
                  ]}
            </Menu>
          </Box>
        )}
      </Box>
      <Modal
        open={deleteConfirmOpen}
        onClose={handleCancelDelete}
        aria-labelledby='delete-confirmation-dialog'
      >
        <Box className='modal-container'>
          <Typography variant='h6' className='modal-title'>
            {t('messages.deleteTodoDialog')}
          </Typography>
          <Box className='modal-button-container'>
            <Button
              id={`${id ?? 'taskItemBlock'}DeleteBtn`}
              onClick={handleConfirmDelete}
              className='modal-delete'
            >
              {t('buttons.delete')}
            </Button>
            <Button
              id={`${id ?? 'taskItemBlock'}NoBtn`}
              onClick={handleCancelDelete}
              className='modal-cancel'
            >
              {t('buttons.no')}
            </Button>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default TaskItem;
